import React, { useEffect } from 'react'
import { GetServerSideProps } from 'next'
import { useTranslation } from 'next-i18next'
import getEMTServerSideProps, { EMTServerSideProps } from '@/helpers/getEMTServerSideProps'
import { computeMessageEventData_Login } from '@/data'
import { SEO, withNoAuthRoute, withNoAuthRouteProps } from '@/components'
import { EmtButton, EmtTextField, EmtTypography } from 'emt-components-v2'
import { useFormValues, useIsResponsive } from '@/hooks'
import { Routes } from '@/assets/configs'
import NextMuiLink from '@/components/NextMuiLink'
import { useEMTDispatch } from '@/store'
import { AuthServiceHelper } from '@/modules/Auth'
import useConnectionRoutes from '@/hooks/useConnectionRoutes'
import { appendParamsIfNeeded, getFirstQueryParam, openUrlApp, Validator } from '@/utils'
import LinkButton from '@/components/EMT/LinkButton'
import SimpleLayout from '@/components/EMT/SimpleLayout'
import Footer from '@/components/EMT/Footer'
import usePetSitterConnectionRoutes from '@/hooks/usePetSitterConnectionRoutes'
import VariantImages from '@/assets/VariantImages'
import useEnterKey from '@/hooks/useEnterKey'
import ConnectionLink from '@/components/ConnectionLink'
import { useRouter } from 'next/router'
import { RegistrationType } from '@/dataV3/User/ConnectedUser'
import { Constants } from '@/assets'
import { usePetsittingMode } from '@/store/usePetsittingMode'

import { getCookie } from 'cookies-next'

import { TokenKeys } from '@/modules/Auth/LocalStorageHelper'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'

type Props = withNoAuthRouteProps & EMTServerSideProps

const stubs = {
  emailOwner: process.env.NEXT_PUBLIC_STUBS_EMAIL_OWNER || '',
  password: process.env.NEXT_PUBLIC_STUBS_PASSWORD || '',
}

type FormValues = {
  email: string
  password: string
}

const FIRST_SUPERSITTER_COOKIE = 'firstConnexionToSuperSittersDone'

const SignInPage: React.FC<Props> = ({ url, title }) => {
  const { t } = useTranslation('connection')
  const [isMobile] = useIsResponsive()
  const dispatch = useEMTDispatch()
  const [enterKeyRefClick] = useEnterKey()
  const router = useRouter()
  const { isPetsittingMode } = usePetsittingMode()

  const selectedService = useNewRequestCartStore((s) => s.selectedService)

  const fromQuery = getFirstQueryParam(router.query.from)

  const { navigateToNextRoute } = useConnectionRoutes()
  const { goNextStep } = usePetSitterConnectionRoutes({ shouldInitialize: false })

  const [isLoading, setLoading] = React.useState<boolean>(false)
  const [formIsSend, setFormIsSend] = React.useState<boolean>(false)

  const { values, errors, setValue, setError, manageNotificationError, isValid } = useFormValues<FormValues>({
    email: stubs.emailOwner,
    password: stubs.password,
  })

  const onFieldChange = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValue(key, value)
    checkField(key, value)
  }

  const checkField = <K extends keyof FormValues>(key: K, value: FormValues[K] = values[key]): boolean => {
    switch (key) {
      case 'password':
        if (!value) {
          setError(key, t(`sign-in.errors.${key}`))
          return false
        }

        setError(key, '')
        return true
      case 'email':
        if (!Validator.validate(value as FormValues['email'], Validator.defaultValidateTypes.mail)) {
          setError(key, t(`sign-in.errors.${key}`))
          return false
        }
        setError(key, '')
        return true
      default:
        return false
    }
  }

  const checkForm = () => {
    let isOK = true

    if (!checkField('email')) {
      isOK = false
    }

    if (!checkField('password')) {
      isOK = false
    }

    return isOK
  }

  const normalizeEmail = (email: string) => email.trim().toLowerCase()

  const onConnectionClick = async () => {
    setFormIsSend(true)
    if (!checkForm()) {
      return
    }
    try {
      setLoading(true)

      const payload = {
        ...values,
        email: normalizeEmail(values.email),
      }

      const me = await AuthServiceHelper.dispatchLoginAsync(dispatch)(payload)

      const firstConnexionCookie = getCookie(FIRST_SUPERSITTER_COOKIE)

      if (isPetsittingMode && me?.['@type'] === 'ConnectedUser' && !firstConnexionCookie) {
        router.push({
          pathname: Routes.Home,
          query: { firstConnexionToSuperSitters: 'true', type: 'dogowner' },
        })

        return
      }

      if (
        me.registrationType === RegistrationType.petsitter &&
        Constants.node_env === 'production' &&
        !isPetsittingMode
      ) {
        const url = `https://app.supersitters.app/pro?token=${encodeURIComponent(
          localStorage.getItem(TokenKeys.Auth ?? '') || '',
        )}&refreshToken=${encodeURIComponent(localStorage.getItem(TokenKeys.Refresh ?? '') || '')}&type=pro`
        openUrlApp(url)
        return
      }

      // Webview tracking
      if (typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined' && me) {
        const eventData = computeMessageEventData_Login('login', me)
        window.ReactNativeWebView.postMessage(JSON.stringify(eventData))
      }

      switch (me.registrationType) {
        default:
          navigateToNextRoute(me)
          break
      }
    } catch (error) {
      manageNotificationError(error)
      console.warn('Sign-In Error', error.message)
    } finally {
      setLoading(false)
    }
  }

  const renderSignInTextField = (key: keyof FormValues, type: string) => (
    <EmtTextField
      fullWidth
      id={key}
      type={type}
      label={t(`sign-in.labels.${key}`)}
      value={values[key]}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange(key, e.target.value)}
      error={formIsSend && !!errors[key]?.length}
      helperText={formIsSend && errors[key]}
    />
  )

  // 🪄 Magic link behavior
  useEffect(() => {
    localStorage.clear()
    const magicTokenQuery = getFirstQueryParam(router.query['magic'])

    if (magicTokenQuery) {
      setLoading(true)
      AuthServiceHelper.dispatchMagicLinkSubmitAsync(dispatch)({ magicLink: magicTokenQuery })
        .then((res) => {
          switch (res.registrationType) {
            case RegistrationType.petsitter:
              goNextStep()
              break
            default:
              navigateToNextRoute(res)
              break
          }
        })
        .catch((error) => {
          manageNotificationError(error)
          console.warn('Sign-In Error', error.message)
        })
        .finally(() => setLoading(false))
    }
  }, [])

  return (
    <>
      <SEO url={`${url}`} subtitle={title} />
      <SimpleLayout
        title={title}
        goBack={
          isPetsittingMode && isMobile
            ? () =>
                fromQuery === 'petsitter-search'
                  ? router.push({
                      pathname: Routes.PetSitter.Search,
                      query: {
                        service: selectedService,
                      },
                    })
                  : router.push(Routes.Connection.WelcomePetsitting)
            : undefined
        }
      >
        {renderSignInTextField('email', 'email')}
        {renderSignInTextField('password', 'password')}
        <EmtTypography variant="caption">
          <NextMuiLink
            href={appendParamsIfNeeded(Routes.Connection.PasswordForgotten, { email: values.email })}
            title={t('sign-in.passwordForgottenAlt') as string}
          >
            {t('sign-in.passwordForgotten')}
          </NextMuiLink>
        </EmtTypography>
        <EmtButton ref={enterKeyRefClick} fullWidth onClick={onConnectionClick} loader={isLoading} disabled={!isValid}>
          {t('sign-in.connectionButton')}
        </EmtButton>

        <ConnectionLink />

        <EmtTypography fontWeight={isMobile ? '700' : '800'} variant={isMobile ? 'h5' : 'h4'}>
          {t('sign-in.noAccount')}
        </EmtTypography>
        <LinkButton href={Routes.Connection.SignUp.Type} fullWidth variant="outlined">
          {t('sign-in.registerButton')}
        </LinkButton>
      </SimpleLayout>
      <Footer
        topDesktopSrc={VariantImages.FooterTopLogin}
        topMobileSrc={VariantImages.FooterTopLogin}
        isLogged={false}
      />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = getEMTServerSideProps({
  namespaces: ['common', 'connection'],
  i18nTitleNs: 'connection',
  i18nTitleKey: 'sign-in.title',
})

export default withNoAuthRoute()(SignInPage)
